import React from "react";
import { DailyPracticeRecord, PracticeMetricStats } from "./types";

/**
 * Renders a subtle placeholder dash for zero or empty values.
 */
export const renderMutedDash = (
  color: string = "rgba(0, 0, 0, 0.25)",
): React.ReactNode => <span style={{ color, userSelect: "none" }}>—</span>;

/**
 * Formats a numeric value as British Pounds with two decimal places.
 */
export const formatCurrencyString = (val?: number | null): string => {
  if (val == null || val === 0) return "£0.00";
  return `£${Number(val).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Formats a currency value as a JSX node with tabular numbers styling.
 */
export const formatCurrency = (
  val?: number | null,
  mutedColor?: string,
): React.ReactNode => {
  if (val == null || val === 0) return renderMutedDash(mutedColor);
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
      {`£${Number(val).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`}
    </span>
  );
};

/**
 * Formats a count value as a formatted integer string with tabular numbers styling.
 */
export const formatCount = (
  val?: number | null,
  mutedColor?: string,
): React.ReactNode => {
  if (val == null || val === 0) return renderMutedDash(mutedColor);
  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      {Number(val).toLocaleString("en-GB")}
    </span>
  );
};

/**
 * Computes aggregate summary metrics for a collection of daily practice records.
 */
export const computePracticeAggregates = (
  items: DailyPracticeRecord[] = [],
) => {
  let totalFilings = 0;
  let totalInvoiced = 0;
  let totalCollected = 0;
  let totalComms = 0;

  for (const item of items) {
    const s: PracticeMetricStats = item.stats || {};
    totalFilings +=
      (s.ct || 0) +
      (s.vat || 0) +
      (s.cis || 0) +
      (s.sA100 || 0) +
      (s.sA800 || 0) +
      (s.sA900 || 0) +
      (s.cs || 0) +
      (s.cf || 0) +
      (s.fps || 0) +
      (s.eps || 0) +
      (s.accountsReport || 0);

    totalInvoiced += s.invoicesAmount || 0;
    totalCollected +=
      (s.receiptsAmount || 0) +
      (s.stripeTotalAmount || 0) +
      (s.goCardlessTotalAmount || 0);

    totalComms += (s.emails || 0) + (s.incoming || 0) + (s.outgoing || 0);
  }

  return {
    totalFilings,
    totalInvoiced,
    totalCollected,
    totalComms,
  };
};
